// ---------------------------------------------------------------------------
// FeedbackButton — Bug/feature reporting FAB + modal
//
// A floating action button (positioned above ChatWidget) that opens a
// feedback modal with type toggle, title, description, and auto-captured
// browser context. Submits via api.submitFeedback().
//
// Features:
//   - FAB with feedback icon, stacked above the chat FAB
//   - Modal overlay with segmented type control (Bug / Feature / Feedback)
//   - Title input (required) and description textarea (required)
//   - Auto-captures: page URL, browser info, viewport size
//   - Success state with "Thank you!" message
//   - All inline styles, no external CSS
// ---------------------------------------------------------------------------

import React, { useCallback, useMemo, useState } from 'react';
import type { FeedbackSubmission } from '../types';
import { useUITL } from './UITLProvider';

export interface FeedbackButtonProps {
  /** Override positioning. Defaults to the config value or 'bottom-right'. */
  position?: 'bottom-right' | 'bottom-left';
  /** Use inline mode (no floating button) -- renders the form directly. */
  inline?: boolean;
  /** Offset from the bottom edge in pixels. Defaults to 90 (above ChatWidget). */
  bottomOffset?: number;
}

type FeedbackType = FeedbackSubmission['feedback_type'];

const FEEDBACK_TYPES: Array<{ value: FeedbackType; label: string; icon: string }> = [
  { value: 'bug', label: 'Bug', icon: '🐛' },
  { value: 'feature', label: 'Feature', icon: '💡' },
  { value: 'feedback', label: 'Feedback', icon: '💬' },
];

export function FeedbackButton({
  position,
  inline,
  bottomOffset = 90,
}: FeedbackButtonProps) {
  const { client, config, theme: t } = useUITL();
  const resolvedPosition =
    position ?? config.position?.feedback ?? 'bottom-right';
  const isRight = resolvedPosition === 'bottom-right';

  // Form state
  const [open, setOpen] = useState(!!inline);
  const [type, setType] = useState<FeedbackType>('feedback');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Auto-capture browser context
  const userContext = useMemo(() => {
    if (typeof window === 'undefined') return {};
    return {
      page_url: window.location.href,
      user_agent: navigator.userAgent,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      screen: `${screen.width}x${screen.height}`,
      language: navigator.language,
      timestamp: new Date().toISOString(),
    };
  }, [open]); // Recalculate when modal opens

  const reset = useCallback(() => {
    setType('feedback');
    setTitle('');
    setDescription('');
    setSubmitted(false);
    setError(null);
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!title.trim() || !description.trim() || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      await client.submitFeedback({
        feedback_type: type,
        title: title.trim(),
        feedback_text: description.trim(),
        page_path:
          typeof window !== 'undefined'
            ? window.location.pathname
            : undefined,
        user_context: userContext,
      });
      setSubmitted(true);
    } catch (err) {
      console.error('[UITL SDK] Feedback submit error:', err);
      setError('Failed to submit feedback. Please try again.');
    } finally {
      setSubmitting(false);
    }
  }, [client, description, title, type, submitting, userContext]);

  const canSubmit = title.trim().length > 0 && description.trim().length > 0 && !submitting;

  // ---------- FAB (collapsed state) ----------
  if (!inline && !open) {
    return (
      <button
        onClick={() => {
          setOpen(true);
          setError(null);
        }}
        aria-label="Send feedback"
        style={{
          position: 'fixed',
          bottom: bottomOffset,
          [isRight ? 'right' : 'left']: 20,
          width: 48,
          height: 48,
          borderRadius: '50%',
          background: t.primaryColor,
          color: '#fff',
          border: 'none',
          cursor: 'pointer',
          fontSize: 20,
          fontFamily: t.fontFamily,
          boxShadow: '0 3px 10px rgba(0,0,0,0.15)',
          zIndex: t.zIndex + 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          transition: 'transform 0.2s',
        }}
        onMouseEnter={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1.08)';
        }}
        onMouseLeave={(e) => {
          (e.currentTarget as HTMLButtonElement).style.transform = 'scale(1)';
        }}
      >
        <span role="img" aria-label="feedback">📝</span>
      </button>
    );
  }

  // ---------- Modal overlay + form ----------
  const modalStyle: React.CSSProperties = inline
    ? { fontFamily: t.fontFamily }
    : {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: t.zIndex + 10,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      };

  const backdropStyle: React.CSSProperties = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0,0,0,0.4)',
    zIndex: t.zIndex + 10,
  };

  const panelStyle: React.CSSProperties = {
    width: inline ? '100%' : 420,
    maxWidth: '95vw',
    maxHeight: '90vh',
    borderRadius: t.borderRadius + 4,
    background: t.backgroundColor,
    boxShadow: inline ? 'none' : '0 12px 40px rgba(0,0,0,0.2)',
    fontFamily: t.fontFamily,
    zIndex: t.zIndex + 11,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
    position: inline ? 'relative' : 'fixed',
    top: inline ? undefined : '50%',
    left: inline ? undefined : '50%',
    transform: inline ? undefined : 'translate(-50%, -50%)',
  };

  // ---------- Success state ----------
  if (submitted) {
    return (
      <>
        {!inline && (
          <div
            style={backdropStyle}
            onClick={() => {
              reset();
              setOpen(false);
            }}
          />
        )}
        <div style={panelStyle}>
          <div
            style={{
              padding: 40,
              textAlign: 'center',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span style={{ fontSize: 48 }}>&#10003;</span>
            <p
              style={{
                fontSize: 20,
                fontWeight: 700,
                margin: 0,
                color: t.textColor,
              }}
            >
              Thank you!
            </p>
            <p
              style={{
                fontSize: 14,
                color: t.textSecondary,
                margin: 0,
                lineHeight: 1.5,
              }}
            >
              Your feedback has been received. We appreciate you taking the time
              to help us improve.
            </p>
            <button
              onClick={() => {
                reset();
                if (!inline) setOpen(false);
              }}
              style={{
                marginTop: 8,
                background: t.primaryColor,
                color: '#fff',
                border: 'none',
                padding: '10px 28px',
                borderRadius: t.borderRadius,
                cursor: 'pointer',
                fontSize: 14,
                fontWeight: 600,
              }}
            >
              {inline ? 'Send More' : 'Close'}
            </button>
          </div>
        </div>
      </>
    );
  }

  // ---------- Form ----------
  return (
    <>
      {!inline && (
        <div style={backdropStyle} onClick={() => setOpen(false)} />
      )}
      <div style={panelStyle}>
        {/* Header */}
        <div
          style={{
            background: t.primaryColor,
            color: '#fff',
            padding: '14px 18px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 16 }}>📝</span>
            <span style={{ fontSize: 15, fontWeight: 600 }}>
              Send Feedback
            </span>
          </div>
          {!inline && (
            <button
              onClick={() => setOpen(false)}
              aria-label="Close feedback form"
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                color: '#fff',
                cursor: 'pointer',
                fontSize: 16,
                width: 28,
                height: 28,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                lineHeight: 1,
              }}
            >
              &#x2715;
            </button>
          )}
        </div>

        {/* Form body */}
        <div
          style={{
            padding: 18,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
            overflowY: 'auto',
          }}
        >
          {/* Type segmented control */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: t.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 6,
                display: 'block',
              }}
            >
              Type
            </label>
            <div
              style={{
                display: 'flex',
                borderRadius: t.borderRadius,
                overflow: 'hidden',
                border: `1px solid ${t.borderColor}`,
              }}
            >
              {FEEDBACK_TYPES.map((ft) => (
                <button
                  key={ft.value}
                  onClick={() => setType(ft.value)}
                  type="button"
                  style={{
                    flex: 1,
                    padding: '8px 0',
                    background:
                      type === ft.value
                        ? t.primaryColor
                        : t.backgroundColor,
                    color: type === ft.value ? '#fff' : t.textColor,
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: type === ft.value ? 600 : 400,
                    fontFamily: t.fontFamily,
                    transition: 'background 0.15s, color 0.15s',
                    borderRight:
                      ft.value !== 'feedback'
                        ? `1px solid ${t.borderColor}`
                        : 'none',
                  }}
                >
                  {ft.icon} {ft.label}
                </button>
              ))}
            </div>
          </div>

          {/* Title input */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: t.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 6,
                display: 'block',
              }}
            >
              Title <span style={{ color: t.errorColor }}>*</span>
            </label>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Brief summary of your feedback"
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: t.borderRadius,
                border: `1px solid ${t.borderColor}`,
                fontSize: 14,
                fontFamily: t.fontFamily,
                color: t.textColor,
                outline: 'none',
                boxSizing: 'border-box',
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = t.primaryColor;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = t.borderColor;
              }}
            />
          </div>

          {/* Description textarea */}
          <div>
            <label
              style={{
                fontSize: 12,
                fontWeight: 600,
                color: t.textSecondary,
                textTransform: 'uppercase',
                letterSpacing: 0.5,
                marginBottom: 6,
                display: 'block',
              }}
            >
              Description <span style={{ color: t.errorColor }}>*</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder={
                type === 'bug'
                  ? 'Steps to reproduce, expected vs actual behavior...'
                  : type === 'feature'
                    ? 'Describe the feature you would like to see...'
                    : 'Tell us what is on your mind...'
              }
              rows={5}
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: t.borderRadius,
                border: `1px solid ${t.borderColor}`,
                fontSize: 14,
                fontFamily: t.fontFamily,
                color: t.textColor,
                resize: 'vertical',
                outline: 'none',
                boxSizing: 'border-box',
                lineHeight: 1.5,
              }}
              onFocus={(e) => {
                e.currentTarget.style.borderColor = t.primaryColor;
              }}
              onBlur={(e) => {
                e.currentTarget.style.borderColor = t.borderColor;
              }}
            />
          </div>

          {/* Auto-captured context note */}
          <p
            style={{
              fontSize: 11,
              color: t.textSecondary,
              margin: 0,
              lineHeight: 1.4,
            }}
          >
            Page URL, browser info, and viewport size are automatically included
            with your submission.
          </p>

          {/* Error message */}
          {error && (
            <div
              style={{
                padding: '8px 12px',
                background: '#FEF2F2',
                color: t.errorColor,
                borderRadius: t.borderRadius - 2,
                fontSize: 13,
                textAlign: 'center',
              }}
            >
              {error}
            </div>
          )}

          {/* Submit button */}
          <button
            onClick={handleSubmit}
            disabled={!canSubmit}
            style={{
              background: t.primaryColor,
              color: '#fff',
              border: 'none',
              padding: '12px 0',
              borderRadius: t.borderRadius,
              cursor: canSubmit ? 'pointer' : 'default',
              fontSize: 14,
              fontWeight: 600,
              fontFamily: t.fontFamily,
              opacity: canSubmit ? 1 : 0.5,
              transition: 'opacity 0.15s, background 0.15s',
            }}
            onMouseEnter={(e) => {
              if (canSubmit) {
                (e.currentTarget as HTMLButtonElement).style.background =
                  t.primaryHover;
              }
            }}
            onMouseLeave={(e) => {
              (e.currentTarget as HTMLButtonElement).style.background =
                t.primaryColor;
            }}
          >
            {submitting ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </div>
      </div>
    </>
  );
}
